/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Shared VIN validation rules and user-facing copy. */
export const VIN_MAX_LENGTH = 17;

// GM VIN: full 17 or last-8; excludes I, O, Q.
export const VIN_RE = /^(?:[A-HJ-NPR-Z0-9]{8}|[A-HJ-NPR-Z0-9]{17})$/;

export const normalizeVin = (value: string): string => value.trim().toUpperCase();

export const isValidVin = (value: string): boolean => VIN_RE.test(normalizeVin(value));

/**
 * Prefer the full VIN AVP resolved; otherwise what the user typed. A last-8 fragment from
 * AVP is never preferred, so it can't replace a full VIN the user typed.
 */
export const canonicalVin = (typed: string, fromDetails?: string | null): string => {
  const fromAvp = fromDetails ? normalizeVin(fromDetails) : '';
  if (fromAvp.length === VIN_MAX_LENGTH && isValidVin(fromAvp)) return fromAvp;
  return typed;
};

// A full 17-character VIN embedded in decoded barcode text (Code 39 / Data Matrix / QR).
const VIN17_RE = /[A-HJ-NPR-Z0-9]{17}/;

/**
 * Pull a full 17-character VIN out of raw barcode text. GM VIN labels encode the VIN as a
 * Code 39 or Data Matrix symbol; some symbologies wrap the VIN with a leading marker or
 * trailing checksum, so we scan for the first 17-char run of valid VIN characters rather than
 * trusting the whole payload. Returns the normalized VIN, or null when no valid VIN is present.
 */
export const extractVin = (raw: string): string | null => {
  const compact = raw.toUpperCase().replace(/\s+/g, '');
  const match = compact.match(VIN17_RE);
  return match && isValidVin(match[0]) ? match[0] : null;
};

/** i18n keys for VIN field copy; render with t(key). */
export const VIN_PLACEHOLDER = 'vin.placeholder';

export const VIN_HINT = 'vin.hint';
/** Diagnostic-program explanation shared by Home and the picker. */
export const DIAG_PROGRAM_HINT = 'vin.diagProgramHint';

/** Diagnostic-trouble-code explanation shared by Home and the picker. */
export const DTC_HINT = 'vin.dtcHint';
